import { useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";

type CarouselMedia = {
  type: "video" | "image";
  url: string;
  fallbackUrl?: string;
};

type CarouselSlide = {
  media?: CarouselMedia;
};

type UseCarouselMediaPreloadParams = {
  slides: CarouselSlide[];
  setResolvedImageUrls: Dispatch<SetStateAction<Record<string, string>>>;
};

export const useCarouselMediaPreload = ({
  slides,
  setResolvedImageUrls,
}: UseCarouselMediaPreloadParams) => {
  useEffect(() => {
    let isCancelled = false;

    const warmImageRequest = (url: string) => {
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = url;
      document.head.appendChild(preload);

      return () => {
        if (preload.parentNode) preload.parentNode.removeChild(preload);
      };
    };

    const cleanups: Array<() => void> = [];

    slides.forEach((slide) => {
      const media = slide?.media;
      if (!media?.url) return;

      if (media.type === "image") {
        cleanups.push(warmImageRequest(media.url));

        const candidateUrls = media.fallbackUrl ? [media.url, media.fallbackUrl] : [media.url];
        let resolved = false;

        candidateUrls.forEach((candidateUrl) => {
          const img = new Image();
          img.decoding = "async";
          img.loading = "eager";

          img.onload = () => {
            if (isCancelled || resolved) return;
            resolved = true;
            setResolvedImageUrls((prev) =>
              prev[media.url] ? prev : { ...prev, [media.url]: candidateUrl },
            );
          };

          img.src = candidateUrl;
        });

        return;
      }

      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = media.url;
    });

    return () => {
      isCancelled = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [setResolvedImageUrls, slides]);
};
